import React from 'react';
import {
  Badge, Button, Checkbox, EmptyState, ErrorState, FilterChip, SkeletonRow, Table,
} from '@parsina/framework';
import {
  History, Pencil, Plus, ShieldOff, SearchX,
} from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { gql } from '../../lib/api';
import { EMPTY } from '../../lib/format';
import { useShortcut } from '../../lib/shortcuts';
import { INSURANCE_CATALOGS } from '../../constants/countryProfile';
import { COUNTRY_INSURERS_QUERY } from './queries';
import {
  CountryCode, countryErrorMessage, insurerNameIn, useCountryName,
} from './shared';
import InsurerDialog from './InsurerDialog';
import InsurerHistoryDialog from './InsurerHistoryDialog';

/**
 * «بیمه‌گرها» — accounting's platform-owned insurer catalogue, by country
 * (PM-0218). A country profile's `insuranceCatalog` names one of these
 * catalogues; a catalogue is its country's rows plus every self-pay row
 * («آزاد»), which belongs to all of them.
 *
 * The filter is the URL's `catalog` (and `inactive`); the BFF filters, and
 * filters again by each row's own country, so an accounting that predates the
 * filter never passes Iran's insurers off as another country's.
 *
 * ── THE EDITOR (PM-0246) ────────────────────────────────────────────────────
 * Where accounting's InsurerAdminService answers (`editorEnabled` — false hides
 * every action; null = the BFF could not tell, and a write then says what is
 * wrong), «افزودن بیمه‌گر» adds an insurer to a country's catalogue (the
 * filter's country preselected; N from the keyboard), «ویرایش» changes one, and
 * «تاریخچه» shows who changed it and why. A self-pay row stays read-only. After
 * a write the list is read again — it is never patched by hand.
 */
export default function InsurersView({ view, onChange }) {
  const { t, locale } = useTranslation();
  const countryName = useCountryName();

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  // The row a dialog is about is kept apart from whether it is open, so a
  // closing dialog does not flip to «add» on its way out.
  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyRow, setHistoryRow] = React.useState(null);

  const variables = { country: view.catalog || null, includeInactive: view.inactive === '1' };
  const key = JSON.stringify(variables);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(COUNTRY_INSURERS_QUERY, JSON.parse(key))
      .then((d) => {
        if (!d?.countryInsurers) throw new Error(t('countries.insurers.error'));
        setData(d.countryInsurers);
      })
      .catch((e) => setError(countryErrorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [key, t]);

  React.useEffect(() => { load(); }, [load]);

  // The catalogues there is data for, plus one the URL names (a deep link to a
  // key accounting does not hold yet is still a question worth answering).
  const catalogs = [...new Set([...INSURANCE_CATALOGS, ...(view.catalog ? [view.catalog] : [])])];
  const insurers = data?.insurers || [];
  // false = the editor does not answer here; null = could not tell — offered.
  const canEdit = !!data?.configured && data.editorEnabled !== false;

  const nameOf = (r) => insurerNameIn(r, locale);

  const openEditor = (row) => {
    setEditRow(row);
    setEditOpen(true);
  };
  const openHistory = (row) => {
    setHistoryRow(row);
    setHistoryOpen(true);
  };

  useShortcut('n', () => openEditor(null), {
    label: t('countries.insurers.editor.createTitle'),
    group: t('nav.items.countries'),
    enabled: canEdit && !editOpen && !historyOpen,
  });

  /** After a write: read the list again; a new insurer outside the filter brings its country's catalogue into view. */
  const saved = (record) => {
    if (record?.country && view.catalog && record.country !== view.catalog && !record.isSelfPay) {
      onChange({ catalog: record.country });
      return;
    }
    load();
  };

  const columns = [
    {
      key: 'name',
      header: t('countries.insurers.columns.name'),
      render: (r) => (
        <span dir="auto" className="truncate font-semibold text-foreground" title={r.nameFa}>{nameOf(r)}</span>
      ),
    },
    {
      key: 'country',
      header: t('countries.insurers.columns.catalog'),
      render: (r) => (r.isSelfPay
        ? <span className="text-muted-foreground">{t('countries.insurers.everyCatalog')}</span>
        : <span className="flex items-center gap-2">{countryName(r.country)} <CountryCode code={r.country} /></span>),
    },
    {
      key: 'kind',
      header: t('countries.insurers.columns.kind'),
      render: (r) => (r.isSelfPay
        ? <Badge variant="outline">{t('countries.insurers.selfPay')}</Badge>
        : t(`countries.insurers.kind.${r.kind}`)),
    },
    {
      key: 'registry',
      header: t('countries.insurers.columns.registry'),
      render: (r) => (r.registryCode ? <code {...LTR_TEXT} className="font-mono text-xs">{r.registryCode}</code> : EMPTY),
    },
    {
      key: 'verify',
      header: t('countries.insurers.columns.verify'),
      render: (r) => (r.verifyProvider ? <code {...LTR_TEXT} className="font-mono text-xs">{r.verifyProvider}</code> : EMPTY),
    },
    {
      key: 'active',
      header: t('countries.insurers.columns.status'),
      render: (r) => (r.isActive
        ? <Badge variant="success">{t('countries.insurers.active')}</Badge>
        : <Badge variant="outline">{t('countries.insurers.inactive')}</Badge>),
    },
    ...(canEdit ? [{
      key: 'actions',
      header: <span className="sr-only">{t('countries.insurers.columns.actions')}</span>,
      align: 'end',
      render: (r) => (r.isSelfPay ? (
        <span
          className="whitespace-nowrap text-xs text-muted-foreground"
          title={t('countries.insurers.selfPayHint')}
          data-testid={`countries-insurer-${r.id}-read-only`}
        >
          {t('countries.insurers.readOnlyRow')}
        </span>
      ) : (
        <span className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openHistory(r)}
            aria-label={t('countries.insurers.actions.historyOf', { name: nameOf(r) })}
            data-testid={`countries-insurer-${r.id}-history`}
          >
            <History size={14} aria-hidden />
            {t('countries.insurers.actions.history')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditor(r)}
            aria-label={t('countries.insurers.actions.editOf', { name: nameOf(r) })}
            data-testid={`countries-insurer-${r.id}-edit`}
          >
            <Pencil size={14} aria-hidden />
            {t('countries.insurers.actions.edit')}
          </Button>
        </span>
      )),
    }] : []),
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3" data-testid="countries-insurers-toolbar">
        <div role="group" aria-label={t('countries.insurers.filterLabel')} className="flex flex-wrap gap-1.5">
          {['', ...catalogs].map((c) => (
            <FilterChip
              key={c || 'all'}
              label={c ? `${countryName(c)} (${c})` : t('countries.insurers.all')}
              active={(view.catalog || '') === c}
              onClick={() => onChange({ catalog: c })}
              data-testid={`countries-insurers-filter-${c || 'all'}`}
            />
          ))}
        </div>
        <Checkbox
          checked={view.inactive === '1'}
          onChange={(v) => onChange({ inactive: v ? '1' : '' })}
          label={t('countries.insurers.showInactive')}
          data-testid="countries-insurers-inactive"
        />
        {canEdit ? (
          <div className="ms-auto flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => openHistory(null)} data-testid="countries-insurers-history">
              <History size={14} aria-hidden />
              {t('countries.insurers.actions.allHistory')}
            </Button>
            <Button size="sm" onClick={() => openEditor(null)} data-testid="countries-insurers-create">
              <Plus size={14} aria-hidden />
              {t('countries.insurers.actions.create')}
            </Button>
          </div>
        ) : data?.configured ? (
          <span className="ms-auto text-xs text-muted-foreground" data-testid="countries-insurers-read-only">
            {t('countries.insurers.readOnly')}
          </span>
        ) : null}
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div data-testid="countries-insurers-loading" data-state="loading" role="status" aria-busy="true" aria-label={t('countries.insurers.loading')}>
            <table className="w-full">
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <SkeletonRow key={i} cols={6} />
                ))}
              </tbody>
            </table>
          </div>
        ) : error ? (
          <ErrorState
            title={t('countries.insurers.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="countries-insurers-error"
          />
        ) : !data.configured ? (
          <EmptyState
            icon={<ShieldOff size={44} strokeWidth={1.5} aria-hidden />}
            title={t('countries.insurers.unconfigured.title')}
            description={t('countries.insurers.unconfigured.desc')}
            data-testid="countries-insurers-unconfigured"
          />
        ) : (
          <div data-state={insurers.length ? 'content' : 'empty'}>
            <Table
              dense
              ariaLabel={t('countries.insurers.aria')}
              columns={columns}
              rows={insurers}
              rowKey={(r) => r.id}
              rowProps={(r) => ({ 'data-testid': `countries-insurer-${r.id}` })}
              empty={(
                <EmptyState
                  icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('countries.insurers.empty.title')}
                  description={t('countries.insurers.empty.desc')}
                  data-testid="countries-insurers-empty"
                />
              )}
              data-testid="countries-insurers-table"
            />
          </div>
        )}
      </div>

      {canEdit ? (
        <>
          <InsurerDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            insurer={editRow}
            defaultCountry={view.catalog || ''}
            catalogs={[...catalogs, ...insurers.filter((r) => !r.isSelfPay).map((r) => r.country)]}
            onSaved={saved}
          />
          <InsurerHistoryDialog
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            insurer={historyRow}
            name={nameOf(historyRow)}
            nameOfId={(id) => {
              const row = insurers.find((r) => r.id === id);
              return row ? nameOf(row) : null;
            }}
          />
        </>
      ) : null}
    </div>
  );
}
